import Config from '../Containers/Config'
import SubtitlesContainer from '../Containers/SubtitlesContainer/SubtitlesContainer'
import audioContext from '../Components/AudioContext'
import AudioNode from '../Components/AudioNode'
import LoadingController from './LoadingController'

type InventoryCombinationAnswer = {
    id: string
    subtitles: string
    audioId: string
}

type RandomAnswerManifest = {
    allIds: Array<string>
    byId: Record<
        string,
        {
            id: string
            srcUrl: string
            subtitles: string
        }
    >
}

export enum RandomAnswerType {
    YES = 'yes',
    NO = 'no',
}

function getRandomElement<T>(array: Array<T>): T | undefined {
    if (array.length === 0) {
        return undefined
    }

    if (array.length === 1) {
        return array[0]
    }

    return array[Math.round(Math.random() * (array.length - 1))]
}

// @ts-ignore -- Safari interop
window.AudioContext = window.AudioContext ?? window.webkitAudioContext

class AudioController {
    private readonly context: AudioContext
    private readonly gainNode: GainNode
    private audioNodes: Record<string, AudioNode> = {}
    private currentActionAudioId?: string

    static NO_AUDIO_FALLBACK_ID = 'NO_AUDIO_FALLBACK_ID'
    private noAudioFallbackTimeOutId?: number
    private noAudioFallbackResolve?: () => void

    private randomYesAnswers: Array<InventoryCombinationAnswer> = []
    private randomNoAnswers: Array<InventoryCombinationAnswer> = []

    constructor(audioContext: AudioContext) {
        this.context = audioContext
        this.gainNode = this.context.createGain()
        this.gainNode.connect(this.context.destination)
    }

    flush = () => {
        const audioNodeIds = [...Object.keys(this.audioNodes)]
        audioNodeIds.forEach((audioNodeId) => {
            this.audioNodes[audioNodeId].stop()
            this.audioNodes[audioNodeId].disconnect()
            delete this.audioNodes[audioNodeId]
        })
        this.audioNodes = {}
        this.randomYesAnswers = []
        this.randomNoAnswers = []
    }

    createAudioNode = (src: string): AudioNode => new AudioNode(src)

    remove = (audioId: string) => {
        delete this.audioNodes[audioId]
    }

    add = (src: string): string => {
        const audioNode = this.createAudioNode(src)
        this.audioNodes[audioNode.id] = audioNode
        this.audioNodes[audioNode.id].connect(this.gainNode)
        return audioNode.id
    }

    addAllToLoadingQueue = async () => {
        // load random answers if we haven't already
        if (this.randomNoAnswers.length < 1 || this.randomYesAnswers.length < 1) {
            const yesAnswerManifest = (await (
                await fetch(Config.RANDOM_YES_MANIFEST_URL)
            ).json()) as RandomAnswerManifest
            const noAnswerManifest = (await (await fetch(Config.RANDOM_NO_MANIFEST_URL)).json()) as RandomAnswerManifest

            if (!yesAnswerManifest || !noAnswerManifest) {
                console.error(
                    '##### Inamove::AudioController => loadAll: Could not load random "yes" or "no" manifest!'
                )
            } else {
                this.randomYesAnswers = yesAnswerManifest.allIds.map((answerId) => {
                    const { id, srcUrl, subtitles } = yesAnswerManifest.byId[answerId]
                    return {
                        id,
                        audioId: this.add(Config.RANDOM_YES_AUDIO_PATH + srcUrl + Config.AUDIO_FILE_EXT),
                        subtitles,
                    }
                })

                this.randomNoAnswers = noAnswerManifest.allIds.map((answerId) => {
                    const { id, srcUrl, subtitles } = noAnswerManifest.byId[answerId]
                    return {
                        id,
                        audioId: this.add(Config.RANDOM_NO_AUDIO_PATH + srcUrl + Config.AUDIO_FILE_EXT),
                        subtitles,
                    }
                })
            }
        }

        Object.keys(this.audioNodes).map(async (audioNodeId) => {
            LoadingController.addToQueue(this.audioNodes[audioNodeId])
        })
    }

    loadAll = async () => {
        // load random answers if we haven't already
        if (this.randomNoAnswers.length < 1 || this.randomYesAnswers.length < 1) {
            const yesAnswerManifest = (await (
                await fetch(Config.RANDOM_YES_MANIFEST_URL)
            ).json()) as RandomAnswerManifest
            const noAnswerManifest = (await (await fetch(Config.RANDOM_NO_MANIFEST_URL)).json()) as RandomAnswerManifest

            if (!yesAnswerManifest || !noAnswerManifest) {
                console.error(
                    '##### Inamove::AudioController => loadAll: Could not load random "yes" or "no" manifest!'
                )
            } else {
                this.randomYesAnswers = yesAnswerManifest.allIds.map((answerId) => {
                    const { id, srcUrl, subtitles } = yesAnswerManifest.byId[answerId]
                    return {
                        id,
                        audioId: this.add(Config.RANDOM_YES_AUDIO_PATH + srcUrl + Config.AUDIO_FILE_EXT),
                        subtitles,
                    }
                })

                this.randomNoAnswers = noAnswerManifest.allIds.map((answerId) => {
                    const { id, srcUrl, subtitles } = noAnswerManifest.byId[answerId]
                    return {
                        id,
                        audioId: this.add(Config.RANDOM_NO_AUDIO_PATH + srcUrl + Config.AUDIO_FILE_EXT),
                        subtitles,
                    }
                })
            }
        }

        return Promise.all(Object.keys(this.audioNodes).map(async (audioNodeId) => this.audioNodes[audioNodeId].load()))
    }

    play = (id: string = AudioController.NO_AUDIO_FALLBACK_ID, offset = 0): Promise<void> =>
        new Promise((resolve) => {
            const audioNode = this.audioNodes[id]

            if (audioNode && audioNode.hasBuffer()) {
                audioNode.play(offset, resolve)
            } else {
                if (id !== AudioController.NO_AUDIO_FALLBACK_ID) {
                    console.error(`>>>>> AudioController: Audio with id "${id}" not found! Using fallback.`)
                    resolve()
                } else {
                    console.error(`>>>>> AudioController: Audio with id "${id}" not found! Using fallback.`)
                    // Why: We need the ability to stop this timer and resolve the play promise even if no audio available
                    this.currentActionAudioId = AudioController.NO_AUDIO_FALLBACK_ID
                    this.noAudioFallbackResolve = resolve
                    this.noAudioFallbackTimeOutId = window.setTimeout(() => {
                        resolve()
                    }, 3000)
                }
            }
        })

    pause = (id: string) => {
        const audioNode = this.audioNodes[id]

        if (audioNode !== undefined) {
            audioNode.pause()
        }
    }

    resume = (id: string) => {
        const audioNode = this.audioNodes[id]

        if (audioNode !== undefined) {
            audioNode.resume()
        }
    }

    stop = (id?: string) => {
        if (id === undefined || this.audioNodes[id] === undefined) {
            return
        }

        if (id === AudioController.NO_AUDIO_FALLBACK_ID) {
            clearTimeout(this.noAudioFallbackTimeOutId)
            this.noAudioFallbackResolve && this.noAudioFallbackResolve()

            this.currentActionAudioId = undefined
            this.noAudioFallbackResolve = undefined
            this.noAudioFallbackTimeOutId = undefined
        } else {
            this.audioNodes[id].stop()
        }
    }

    loop = (id: string, offset?: number) => {
        const audioNode = this.audioNodes[id]

        if (audioNode !== undefined) {
            audioNode.loop(offset)
        }
    }

    // stop here must reject
    playActionAudio = async (id?: string, subtitles?: string) => {
        if (this.currentActionAudioId !== id) {
            this.stopCurrentActionAudio()
            this.currentActionAudioId = id
            SubtitlesContainer.setText(subtitles)
            await this.play(id)

            // WHY: only reset if not intercepted by another action audio.
            // Otherwise it would interfere with the next (intercepting) action audio's subtitles
            if (this.currentActionAudioId !== undefined && this.currentActionAudioId === id) {
                SubtitlesContainer.clear()
                this.currentActionAudioId = undefined
            }
        } else {
            this.stopCurrentActionAudio()
        }
    }

    stopCurrentActionAudio = () => {
        if (this.currentActionAudioId !== undefined) {
            this.stop(this.currentActionAudioId)
            SubtitlesContainer.clear()
            this.currentActionAudioId = undefined
        }
    }

    playRandomAnswer = async (answerType: RandomAnswerType) => {
        const answerRepo = answerType === RandomAnswerType.YES ? this.randomYesAnswers : this.randomNoAnswers

        const answer = getRandomElement(answerRepo)

        if (answer === undefined) {
            console.error(`##### Inamove::AudioController => playRandomAnswer: No answer found for "${answerType}"!`)
            return
        }

        await this.playActionAudio(answer.audioId, answer.subtitles)
    }

    /**
     * Set mute for all Audio handled by this AudioController.
     */
    setMute = (mute: boolean) => {
        this.gainNode.gain.setValueAtTime(mute ? 0 : 1, 0)
    }

    /**
     * Set mute for a single AudioNode.
     */
    setMuteForAudio = (audioNodeId: string, mute: boolean) => {
        const audioNode = this.audioNodes[audioNodeId]

        if (audioNode !== undefined) {
            audioNode.setMute(mute)
        }
    }
}

export default new AudioController(audioContext)
